import useQueries from "@/hooks/useQueries";
import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from "react";
import useEntities from "./createOrder/useEntities";
import useDateFilter from "./useDateFilter";
import { useDebounce } from "./useDebounce";

const fetchExpenses = async (sessionId: any, query: string, licenceId: any) => {
    const response = await axios.post(
        API_URL,
        { q: query },
        {
            headers: { 'Content-Type': 'application/json' },
            params: { t: 'sel', phone: '', id: licenceId, s: sessionId, btn: 'w_itm', api: 'itqan' },
        }
    );
    return response.data;
};

const escapeSqlValue = (value: string) => value.replace(/'/g, "''");
const PAGE_SIZE = 20;

export default function useExpensesController() {
    const queries = useQueries();
    const sessionId = Cookies.get('sessionId');
    const licenceId = localStorage.getItem("licence_id");

    const [selectedProject, setSelectedProject] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);

    const { projectsWithBusiness } = useEntities();
    const { date, setDate, handleQuickSelect } = useDateFilter();
    const debouncedSearchValue = useDebounce(searchValue, 1000);

    const filters = useMemo(() => {
        const conditions: string[] = [];

        if (date.from) {
            const fromQuery = date.from.includes(' ') ? date.from : `${date.from} 00:00:00`;
            conditions.push(`AND money.moneydate >= '${escapeSqlValue(fromQuery)}'`);
        }
        if (date.to) {
            const toQuery = date.to.includes(' ') ? date.to : `${date.to} 23:59:59`;
            conditions.push(`AND money.moneydate <= '${escapeSqlValue(toQuery)}'`);
        }
        if (selectedProject) {
            conditions.push(`AND money.moneyprjid = '${escapeSqlValue(selectedProject)}'`);
        }
        if (debouncedSearchValue.trim()) {
            const s = escapeSqlValue(debouncedSearchValue.trim());
            conditions.push(`AND accounts.accountstring LIKE '%${s}%'`);
        }

        return conditions.join(' ');
    }, [date, selectedProject, debouncedSearchValue]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearchValue, selectedProject, date]);

    const expensesListQuery = queries?.expensesListQuery
        ? buildQuery(queries.expensesListQuery, { filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
        : '';

    const expensesListCountQuery = queries?.expensesListCountQuery
        ? buildQuery(queries.expensesListCountQuery, { filters })
        : '';

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['expenses_list', expensesListQuery, licenceId, sessionId],
        queryFn: () => fetchExpenses(sessionId, expensesListQuery, licenceId),
        enabled: !!expensesListQuery && !!sessionId && !!licenceId,
        staleTime: 0,
        refetchOnMount: true,
        placeholderData: (prev: any) => prev,
    });

    const { data: countData } = useQuery({
        queryKey: ['expenses_count', expensesListCountQuery, licenceId, sessionId],
        queryFn: () => fetchExpenses(sessionId, expensesListCountQuery, licenceId),
        enabled: !!expensesListCountQuery && !!sessionId && !!licenceId,
        staleTime: 0,
    });

    const expenses = data?.data?.result || [];
    const totalCount = Number(countData?.data?.result?.[0]?.total || 0);
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const totalExpensesAmount = useMemo(() => {
        return expenses.reduce((sum: number, row: any) => sum + Number(row?.amount || 0), 0);
    }, [expenses]);

    const isSearching = (searchValue !== debouncedSearchValue) || isFetching;

    return {
        expenses, isLoading, isSearching,
        date, setDate, handleQuickSelect,
        projectsWithBusiness, selectedProject, setSelectedProject,
        searchValue, setSearchValue,
        page, setPage, totalPages, pageSize: PAGE_SIZE, totalOrders: totalCount,
        totalExpensesAmount,
    };
}